/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Toast,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { UserOrganisation } from '../types/types';
import HumanRightsQuestionnaire from '../../src/assets/HumanRightsQuestionnaire.png';
import EnvironmentalProtection from '../../src/assets/EnvironmentalProtection.png';
import ESGDueDilligence from '../../src/assets/ESGDueDiligence.png';
import ESGDueDiligence1 from '../../src/assets/ESGDueDiligence1.png';
import CSDDDQuestionnaire from '../../src/assets/CSDDD-questionnaire.jpg';
import FDVQuestionnaire from '../../src/assets/FDVQuestionnaire.jpg';
import ESGBadge from '../../src/assets/ESGBadgRround.png';
import CSDDDBadge from '../../src/assets/CSDDDBadgeRound.png';
import CSRDBadge from '../../src/assets/CSRDBadgeRound.png';
import LKSGBadge from '../../src/assets/LkSGBadgeRound.png';
import FDVBadge from '../../src/assets/FDVBadgeRound.png';

import { checkSelfAssessmentTemplateEnabled } from '../util/util';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useRecordPageView } from '../hooks/useRecordPageView';
import { captureSupabaseError } from '../lib/sentry';
import { tracking } from '../util/tracking';

const templateImages: Record<string, string> = {
  human_rights_labour: HumanRightsQuestionnaire,
  env_protection: EnvironmentalProtection,
  supplier_esg_report: ESGDueDilligence,
  supplier_lksg_report: ESGDueDiligence1,
  supplier_csddd_report: CSDDDQuestionnaire,
  supplier_fdv_report: FDVQuestionnaire,
  // Add more templates as needed
};

const templateBadges: Record<string, string> = {
  supplier_esg_report: ESGBadge,
  supplier_lksg_report: LKSGBadge,
  supplier_csddd_report: CSDDDBadge,
  supplier_fdv_report: FDVBadge,
  supplier_csrd_report: CSRDBadge,
};

export type CreateAssessmentResponse = {
  id: string;
  status: string;
  completed_at: string | null;
  is_existing: boolean;
};

const SelfAssessment = () => {
  const selectedOrganisation = useSelector(
    (state: any) => state.organisation.selected
  ) as UserOrganisation | undefined;

  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingAssessment, setCreatingAssessment] = useState(false);
  const [toastError, setToastError] = useState<string | null>(null);

  useRecordPageView();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!selectedOrganisation?.organisation?.id) return;

      setLoading(true);
      setToastError(null);

      const { data, error } = await supabase.rpc(
        'get_self_assessment_templates',
        { org_id: selectedOrganisation.organisation.id }
      );

      setLoading(false);

      if (error) {
        console.error('Error fetching templates:', error);
        setToastError('Failed to load templates.');
      } else {
        setTemplates(data || []);
      }
    };

    fetchTemplates();
  }, [selectedOrganisation]);

  const startAssessment = async (templateId: string) => {
    setCreatingAssessment(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        'get-or-create-assessment',
        {
          body: {
            org_id: selectedOrganisation?.organisation?.id,
            tmpl_id: templateId,
          },
        }
      );

      setCreatingAssessment(false);

      if (error) {
        console.error('Error:', error);
        setToastError('Failed to start assessment.');
        return;
      }

      const assessment: CreateAssessmentResponse = data;

      if (
        assessment.status === 'completed' &&
        assessment.completed_at &&
        assessment.is_existing
      ) {
        // TODO show modal instead of toast
        setToastError(
          'You have completed this assessment within the last 30 days. Please come back later.'
        );
      } else if (assessment.id) {
        navigate(`/assessment/${assessment.id}`);
      }
    } catch (error) {
      captureSupabaseError(error, { op: 'assessment.start' });
      console.error('Error:', error);
      setToastError('Failed to start assessment. Please try again.');
      setCreatingAssessment(false);
    }
  };

  const essentialTemplates = templates.filter(
    (t) => t.key && templateImages[t.key]
  );

  const extendedTemplates = templates.filter(
    (t) => !t.key || !templateImages[t.key]
  );

  return (
    <Container fluid>
      <h3>Your self assessment questionnaires</h3>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : null}

      <p>
        To get your certification, please complete the following assessments:
      </p>

      <Tabs defaultActiveKey="essential" className="mb-4">
        <Tab eventKey="essential" title="Essential">
          <Row xs={1} sm={2} md={3} lg={3} className="g-4 mt-2">
            {essentialTemplates.map((template) => {
              const assessmentEnabled = checkSelfAssessmentTemplateEnabled(
                selectedOrganisation?.organisation?.plan?.features ?? null,
                template.key
              );

              const imageSrc =
                template.key && templateImages[template.key]
                  ? templateImages[template.key]
                  : HumanRightsQuestionnaire;

              const badgeSrc =
                template.key && templateBadges[template.key]
                  ? templateBadges[template.key]
                  : ESGBadge;

              const latest = template.latest_assessment;

              let buttonLabel = 'Start';
              let disabled = !assessmentEnabled;
              let buttonVariant = 'primary';
              let notAvailable = false;

              if (latest) {
                if (latest.completed_at) {
                  const completedDate = new Date(latest.completed_at);
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

                  if (completedDate > thirtyDaysAgo) {
                    const date = completedDate.toLocaleDateString();
                    buttonLabel = `Completed on ${date}`;
                    notAvailable = true;
                  } else {
                    buttonLabel = 'Retake';
                    disabled = false;
                    buttonVariant = 'primary';
                  }
                } else {
                  buttonLabel = 'Continue';
                  disabled = false;
                  buttonVariant = 'outline-primary';
                }
              }

              return (
                <Col key={template.id}>
                  <Card className="h-100">
                    <Card.Img
                      variant="top"
                      src={imageSrc}
                      alt={template.title}
                      style={{
                        filter: assessmentEnabled ? 'none' : 'opacity(0.5)',
                      }}
                    />
                    {badgeSrc && (
                      <img
                        src={badgeSrc}
                        alt={`${template.title} badge`}
                        style={{
                          width: '100px',
                          height: '100px',
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                        }}
                      />
                    )}
                    <Card.Body>
                      <Card.Title>{template.title}</Card.Title>
                      <p>Questions: {template.question_count}</p>
                      <p>
                        This assessment awards the following badge(s) upon
                        completion:
                        {template.tags?.map((t: any) => (
                          <Badge key={t} bg="primary" className="mx-1">
                            {t.toUpperCase()}
                          </Badge>
                        ))}
                      </p>
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-space-between">
                      {!assessmentEnabled ? (
                        <span className="text-muted">
                          This assessment is not available on your plan.
                        </span>
                      ) : latest &&
                        !latest.completed_at &&
                        latest.created_at ? (
                        <span className="text-muted">
                          Started:{' '}
                          {new Date(latest.created_at).toLocaleDateString()}
                        </span>
                      ) : notAvailable && latest?.completed_at ? (
                        <span className="text-muted">
                          Completed. You can retake this assessment on{' '}
                          {(() => {
                            const completedDate = new Date(latest.completed_at);
                            const retakeDate = new Date(
                              completedDate.getTime() + 30 * 24 * 60 * 60 * 1000
                            );
                            return retakeDate.toLocaleDateString();
                          })()}{' '}
                        </span>
                      ) : null}
                      {!notAvailable && (
                        <Button
                          variant={buttonVariant}
                          size="sm"
                          className="ms-auto"
                          disabled={creatingAssessment || disabled}
                          onClick={() => {
                            tracking.log('UI', 'self_assessment_start', {
                              templateId: template.id,
                              action: buttonLabel,
                            });
                            startAssessment(template.id);
                          }}
                        >
                          {creatingAssessment ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            buttonLabel
                          )}
                        </Button>
                      )}
                    </Card.Footer>
                  </Card>
                </Col>
              );
            })}

            {essentialTemplates.length === 0 && (
              <Col>
                <p>No default templates available.</p>
              </Col>
            )}
          </Row>
        </Tab>
        <Tab eventKey="extended" title="Extended">
          <div className="mt-3">
            {extendedTemplates.map((template) => {
              const assessmentEnabled = checkSelfAssessmentTemplateEnabled(
                selectedOrganisation?.organisation?.plan?.features ?? null,
                template.key
              );

              const latest = template.latest_assessment;

              let buttonLabel = 'Start';
              let disabled = !assessmentEnabled;

              if (latest) {
                if (latest.completed_at) {
                  buttonLabel = 'Retake';
                  disabled = false;
                } else {
                  buttonLabel = 'Continue';
                  disabled = false;
                }
              }

              return (
                <Card key={template.id} className="mb-3">
                  <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">{template.title}</h6>
                      <small className="text-muted">
                        Questions: {template.question_count}
                      </small>
                    </div>
                    <Button
                      size="sm"
                      disabled={creatingAssessment || disabled}
                      onClick={() => {
                        tracking.log('UI', 'self_assessment_start', {
                          templateId: template.id,
                          action: buttonLabel,
                        });
                        startAssessment(template.id);
                      }}
                    >
                      {buttonLabel}
                    </Button>
                  </Card.Body>
                </Card>
              );
            })}
            {extendedTemplates.length === 0 && (
              <p>No extended templates available.</p>
            )}
          </div>
        </Tab>
      </Tabs>

      {toastError && (
        <Toast
          onClose={() => setToastError(null)}
          show={!!toastError}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastError}</Toast.Body>
        </Toast>
      )}
    </Container>
  );
};

export default SelfAssessment;
